import {
  GoAFormItem,
  GoAInputText,
  GoARadioGroup,
  GoARadioItem,
  GoASpacer,
} from '@abgov/react-components';
import { useEffect, useState } from 'react';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import {
  defaultValidate,
  WizardButtonGroup,
} from '../wizard/wizard-button-group-component';
import { AppDispatch, RootState } from '../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import { Collision, DamageSelection } from '../../models/collision';
import { InitialPointOfImpact } from '../../models/initial-point-of-impact';
import { DamageCardComponent } from '../damage/damage-card';
import { removeAllValidationErrorNotifications } from '../../state/slices/notificationSlice';
import { updateCollision } from '../../state/slices/collisionSlice';
import {
  isAttachment,
  isOtherAttachmentType,
} from '../../state/slices/configurationSlice';
import {
  getReturnToValue,
  IsInReviewEditMode,
} from '../wizard/wizard-component';

interface AttachmentTypeComponentProps extends BaseWizardProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export const AttachmentTypeComponent = ({
  navigationConfiguration,
}: AttachmentTypeComponentProps) => {
  const dispatch = useAppDispatch();

  const defaultSelection = '0';
  const errorText =
    'Choose one attachment/load type above in order to continue';
  const errorTextBoxText =
    'Provide a description in the Other/specify input field';

  const [formError, setFormError] = useState('');
  const [otherFormError, setOtherFormError] = useState('');

  const [selection, setSelection] = useState(defaultSelection);

  const [nextPage, setNextPage] = useState('');

  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );

  const stateCollision = useAppSelector((state: RootState) => state.collision);

  const [poiCards, setPoiCards] = useState<JSX.Element[]>([]);

  const [damageSelectionsList, setDamageSelectionsList] = useState<
    DamageSelection[]
  >(stateCollision?.collision?.vehicles?.[0]?.damage?.damageSelection || []);

  const [attachmentDetails, setAttachmentDetails] = useState(
    stateCollision.collision.vehicles![0].damage?.attachmentDetails,
  );

  const [
    initialPointOfImpactLocationList,
    setInitialPointOfImpactLocationList,
  ] = useState<InitialPointOfImpact[]>([]);

  useEffect(() => {
    const pois: InitialPointOfImpact[] = [];

    stateConfiguration.configuration.pointOfImpact.forEach((poi: any) => {
      pois.push(new InitialPointOfImpact(poi.id, poi.value));
    });

    setInitialPointOfImpactLocationList(pois);
  }, [stateConfiguration.configuration.pointOfImpact]);

  useEffect(() => {
    if (damageSelectionsList && damageSelectionsList.length > 0) {
      let cardCount = 0;
      const cardList: JSX.Element[] = [];

      const arr = [...damageSelectionsList];
      arr!
        .sort((a, b) => a.clickTime! - b.clickTime!)
        .map((damageSelector) => {
          cardCount++;

          if (
            isAttachment(
              parseInt(damageSelector.pointOfImpactId),
              stateConfiguration,
            )
          ) {
            cardList.push(
              <div key={damageSelector!.pointOfImpactId}>
                <DamageCardComponent
                  id={parseInt(damageSelector!.pointOfImpactId!)}
                  order={cardCount}
                  helperText={
                    !damageSelector!.isInitialPointOfImpact
                      ? 'You indicated attachment/load damage'
                      : ''
                  }
                  showRemoveButton={false}
                  isInitalPointOfImpact={damageSelector!.isInitialPointOfImpact}
                  initialPointsOfImpact={initialPointOfImpactLocationList}
                  navigateHandler={() => {
                    window.location.href = `#damage`;
                  }}
                  removeHandler={() => {}}
                />
              </div>,
            );
          }
        });
      setPoiCards(cardList);
    }
  }, [initialPointOfImpactLocationList, damageSelectionsList]);

  useEffect(() => {
    if (stateCollision.collision.vehicles![0].damage?.attachment) {
      setSelection(
        stateCollision.collision.vehicles![0].damage.attachment.toString() ??
          '0',
      );
    }
  }, [stateCollision]);

  useEffect(() => {
    const pageConfig = navigationConfiguration.pagingConfiguration.find(
      (item) => item.default !== undefined,
    );

    setNextPage(pageConfig?.default ?? 'error');
  }, []);

  const save = () => {
    if (selection) {
      let updatedCollision: Collision = { ...stateCollision.collision };

      if (
        navigationConfiguration.updateMetadata &&
        navigationConfiguration.stepId
      ) {
        const stepUpdated = navigationConfiguration.updateMetadata(
          updatedCollision,
          navigationConfiguration.stepId!,
          false,
          true,
        );

        if (stepUpdated && updatedCollision.metadata) {
          updatedCollision.metadata = {
            ...updatedCollision.metadata,
            formState: stepUpdated,
          };
        }
      }

      updatedCollision.vehicles = [
        {
          ...stateCollision.collision.vehicles![0],
          damage: {
            ...stateCollision.collision.vehicles?.[0].damage,
            attachment: selection,
            attachmentDetails: attachmentDetails,
          },
        },
      ];

      dispatch(updateCollision(updatedCollision));
    }
    if (navigationConfiguration.save) {
      navigationConfiguration.save();
    }
  };

  const validate = () => {
    if (
      isOtherAttachmentType(parseInt(selection), stateConfiguration) &&
      (!attachmentDetails || attachmentDetails.length == 0)
    ) {
      setOtherFormError(errorTextBoxText);
      return false;
    }

    return defaultValidate(
      selection,
      defaultSelection,
      errorText,
      setFormError,
      navigationConfiguration.errorNotification,
    );
  };

  const getAttachmentDetailsLabel = (): string => {
    const remainingCharacters =
      50 - (attachmentDetails != undefined ? attachmentDetails.length : 0);
    return `${remainingCharacters} characters remaining`;
  };

  return (
    <div>
      <h1>Vehicle damage</h1>
      The collision details section is the starting point for drivers to
      identify the type of collision they were involved in. In some cases you’ll
      be prompted to add some additional details depending on your type of
      collision.
      <h2>Indicate the type of attachment</h2>
      <GoASpacer vSpacing="xl" />
      <div>{poiCards}</div>
      <GoASpacer vSpacing="l" />
      From the list below, choose the type of attachment that was damaged
      <GoASpacer vSpacing="m" />
      <GoAFormItem error={formError}>
        <GoARadioGroup
          error={formError.length > 0}
          name="attachmentRadioGroup"
          value={selection}
          onChange={(selection, id) => {
            setSelection(id);
            setFormError('');
            setOtherFormError('');
            if (
              isOtherAttachmentType(parseInt(selection), stateConfiguration)
            ) {
              setAttachmentDetails('');
            }

            dispatch(removeAllValidationErrorNotifications());
          }}
        >
          <GoARadioItem
            value={stateConfiguration.configuration.attachments
              .find((x) => {
                return x.value.toLowerCase() == 'recreation trailer'; // this spelling discrpetency is correct based on the ecollision data
              })
              ?.id.toString()}
            label="Recreational trailer"
            description={
              <span style={{ font: 'var(--goa-typography-body-xs)' }}>
                for example non-collapsible trailers such as travel trailer, or
                collapsible/ folding trailers such as tent trailer
              </span>
            }
          ></GoARadioItem>
          <GoARadioItem
            value={stateConfiguration.configuration.attachments
              .find((x) => {
                return x.value.toLowerCase() == 'small utility trailer';
              })
              ?.id.toString()}
            label="Small utility trailer"
            description={
              <span style={{ font: 'var(--goa-typography-body-xs)' }}>
                for example U-Haul trailer or any box or flatbed trailer used to
                transport goods, equipment, horses or livestock"
              </span>
            }
          ></GoARadioItem>
          <GoARadioItem
            value={stateConfiguration.configuration.attachments
              .find((x) => {
                return x.value.toLowerCase() == 'farm equipment';
              })
              ?.id.toString()}
            label="Farm equipment"
            description={
              <span style={{ font: 'var(--goa-typography-body-xs)' }}>
                for example seed drill, plow, cultivator, bale"
              </span>
            }
          ></GoARadioItem>
          <GoARadioItem
            value={stateConfiguration.configuration.attachments
              .find((x) => {
                return x.value.toLowerCase() == 'towed motor vehicle';
              })
              ?.id.toString()}
            label="Towed vehicle"
          ></GoARadioItem>
          <div className="goa-input-reveal">
            <GoAFormItem error={otherFormError}>
              <GoARadioItem
                value={stateConfiguration.configuration.attachments
                  .find((x) => {
                    return x.value.toLowerCase() == 'other/specify';
                  })
                  ?.id.toString()}
                label="Other/specify"
                error={errorTextBoxText.length > 0}
              ></GoARadioItem>
            </GoAFormItem>
          </div>
        </GoARadioGroup>
        <div
          style={{
            display: isOtherAttachmentType(
              parseInt(selection),
              stateConfiguration,
            )
              ? 'block'
              : 'none',
          }}
        >
          {' '}
          <div style={{ display: 'flex', height: '100px' }}>
            <div
              style={{
                backgroundColor: 'var(--Color-Greyscale-200, #DCDCDC)',
                width: '4px',
                marginLeft: '10px',
              }}
            ></div>
            <div style={{ marginLeft: '15px' }}>
              <GoAFormItem
                label={getAttachmentDetailsLabel()}
                labelSize="regular"
              >
                <GoAInputText
                  value={attachmentDetails}
                  name="item"
                  onChange={(item, value) => {
                    setAttachmentDetails('');
                    setAttachmentDetails(value);
                  }}
                  maxLength={50}
                  width="550px"
                  error={
                    isOtherAttachmentType(
                      parseInt(selection),
                      stateConfiguration,
                    ) &&
                    attachmentDetails != undefined &&
                    attachmentDetails.length == 0
                  }
                ></GoAInputText>
              </GoAFormItem>
            </div>
          </div>
        </div>
      </GoAFormItem>
      <WizardButtonGroup
        nextPage={nextPage}
        validate={(): boolean => {
          return validate();
        }}
        save={save}
        navigate={navigationConfiguration.navigate}
        showViewSummaryButton={!IsInReviewEditMode()}
      />
    </div>
  );
};
